/**
 * Executive Dashboard
 * High-level KPI grid with trend charts
 */

import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import MetricCard from '../components/dashboard/MetricCard';
import TrendChart from '../components/dashboard/TrendChart';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import Spinner from '../components/shared/Spinner';
import EmptyState from '../components/shared/EmptyState';
import RefreshButton from '../components/shared/RefreshButton';
import useDateRange from '../hooks/useDateRange';
import { getExecutiveKPIs } from '../services/analyticsService';
import { getDashboardSSOT } from '../services/sentimentService';

const ExecutiveDashboard = () => {
  const { from, to, onChange } = useDateRange();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [ssot, setSsot] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [result, ssotRes] = await Promise.all([
        getExecutiveKPIs({ from_date: from, to_date: to }),
        getDashboardSSOT({ from, to }),
      ]);
      if (result.success) {
        setData(result.data);
      } else {
        toast.error(result.error);
      }
      if (ssotRes.success) setSsot(ssotRes.data);
    } catch (err) {
      toast.error('Failed to load executive KPIs');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Spinner />;
  if (!data) return <EmptyState />;

  const trendData = data.trends || [];
  const kpis = data.kpis || data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h1 className="text-xl font-bold text-gray-800">Executive Dashboard</h1>
          <RefreshButton onClick={fetchData} loading={loading} />
        </div>
        <DateRangeFilter from={from} to={to} onChange={onChange} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <MetricCard label="Est. CSAT" value={kpis.csat != null ? `${kpis.csat}%` : '-'} change={kpis.csat_change} changeLabel="vs prior" sparkData={kpis.csat_spark} color="#22c55e" info="Estimated from average sentiment: (sentiment + 1) × 50, mapping −1..+1 to 0..100%." />
        <MetricCard label="Est. NPS" value={kpis.nps != null ? kpis.nps : '-'} change={kpis.nps_change} changeLabel="vs prior" color="#3b82f6" info="Sentiment proxy: % promoters (sentiment > 0.3) minus % detractors (sentiment < −0.3)." />
        <MetricCard label="Avg Handle Time" value={ssot?.avg_handle_time != null ? `${Math.round(ssot.avg_handle_time)}s` : '-'} color="#f59e0b" />
        <MetricCard label="Resolution Rate" value={ssot?.resolution_rate != null ? `${ssot.resolution_rate}%` : (kpis.resolution_rate != null ? `${kpis.resolution_rate}%` : '-')} change={kpis.resolution_rate_change} changeLabel="vs prior" sparkData={kpis.resolution_spark} color="#8b5cf6" info="Resolved dispositions ÷ total interactions, capped at 100%." />
        <MetricCard label="At-Risk Rate" value={kpis.escalation_rate != null ? `${kpis.escalation_rate}%` : '-'} change={kpis.escalation_rate_change} changeLabel="vs prior" color="#ef4444" info="Escalations ÷ total interactions, capped at 100%." />
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TrendChart
          title="Customer Satisfaction Trend"
          data={trendData}
          xKey="date"
          lines={[
            { dataKey: 'csat', color: '#22c55e', label: 'CSAT' },
            { dataKey: 'nps', color: '#3b82f6', label: 'NPS' },
          ]}
        />
        <TrendChart
          title="Resolution & Risk"
          data={trendData}
          xKey="date"
          lines={[
            { dataKey: 'resolution', color: '#8b5cf6', label: 'Resolution %' },
            { dataKey: 'escalation', color: '#ef4444', label: 'At-Risk %' },
          ]}
        />
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
